import {
  Box,
  FontWeight,
  Icon,
  P,
  Text,
  useTheme,
  Space,
  XYGrid,
  Visibility,
  Glyph,
  ColorPreset,
  PlainButton,
  HoverEffect,
} from "@gocardless/flux-react";
import { i18n } from "@lingui/core";
import { t, Trans } from "@lingui/macro";

import SectionHeader from "../section-header/SectionHeader";

import { usePrimaryCreditor } from "src/libraries/creditor/hooks";
import { Image } from "src/components/images";

interface Props {
  internationalPaymentsEnabled: boolean;
  handleAddBanks: () => void;
  title?: string;
}

const InternationalPaymentsOptions: React.FC<Props> = (props) => {
  const creditor = usePrimaryCreditor();

  const { theme } = useTheme();
  const { internationalPaymentsEnabled, handleAddBanks, title } = props;

  const gridBoxStyle = {
    display: "grid",
    gridTemplateRows: "50px 1fr 25px",
    padding: theme.spacing([0, 2]),
  };

  if (!creditor?.geo) return null;

  const australianMerchant = creditor.geo === "AU";
  if (!internationalPaymentsEnabled && !australianMerchant) return null;

  const internationalPaymentsSectionDescription = australianMerchant
    ? i18n._(
        t({
          id: "setup.bank-details.collection-international-payments-description-au",
          message:
            "We offer the following option for how you can receive international payments:",
        })
      )
    : i18n._(
        t({
          id: "setup.bank-details.collection-international-payments-description",
          message:
            "We offer two options for how you receive international payments:",
        })
      );

  const option2TitleText = australianMerchant ? (
    <Trans id="setup.bank-details.bank-options.option2-title-au">
      Add different currency bank accounts
    </Trans>
  ) : (
    <Trans id="setup.bank-details.bank-options.option2-title">
      Option 2: Add different currency bank accounts
    </Trans>
  );

  return (
    <>
      <SectionHeader
        title={
          title ||
          i18n._(
            t({
              id: "setup.bank-details.collecting-international-payments",
              message: "Collecting international payments?",
            })
          )
        }
        description={internationalPaymentsSectionDescription}
      />
      <Space v={3} />
      <Box
        borderRadius={1.5}
        gutterV={2}
        borderColor={ColorPreset.BorderOnLight_05}
        borderWidth={1}
        bg={ColorPreset.BackgroundLight_01}
      >
        <XYGrid
          templateColumns={
            australianMerchant ? ["1fr"] : ["1fr", null, "1fr 1fr"]
          }
        >
          {australianMerchant ? null : (
            <Box
              css={{
                ...gridBoxStyle,
                borderRight: `1px solid ${theme.color(
                  ColorPreset.BorderOnLight_05
                )}`,
              }}
            >
              <Box data-testid="international-payment-wise-option">
                <Text size={3} weight={FontWeight.Bold}>
                  <Trans id="setup.bank-details.bank-options.option1_title">
                    Option 1: Get paid into your domestic account
                  </Trans>
                </Text>
              </Box>

              <Box spaceBelow={2} spaceAbove={1}>
                <P size={2} color={ColorPreset.TextOnLight_02}>
                  <Trans id="setup.bank-details.bank-options.option1_description">
                    The easiest way to collect international payments.
                    Gocardless will handle the currency conversion with the
                    fairest exchange rates, powered by Wise.
                  </Trans>
                </P>
                <Space v={1} />

                <Image name="WiseLogo" width="72px" height="18px" />
              </Box>

              <Box>
                <Icon
                  name={Glyph.Tick}
                  size="10px"
                  css={{
                    marginRight: "7px",
                    position: "relative",
                    bottom: "2px",
                  }}
                />

                <Text size={3} weight={FontWeight.Bold}>
                  <Trans id="setup.bank-details.bank-options.gbp_account_added">
                    Account added
                  </Trans>
                </Text>
              </Box>
              <Visibility visible={["block", null, "none"]}>
                <Space v={2} />
                <Box height="1px" bg={ColorPreset.BorderOnLight_05} />
                <Space v={2} />
              </Visibility>
            </Box>
          )}
          <Box
            css={{
              ...gridBoxStyle,
            }}
          >
            <Box>
              <Text size={3} weight={FontWeight.Bold}>
                {option2TitleText}
              </Text>
            </Box>

            <Box spaceBelow={2} spaceAbove={1}>
              <P size={2} color={ColorPreset.TextOnLight_02}>
                {creditor.geo === "GB" && (
                  <Trans id="setup.bank-details.bank-options.option2-description">
                    Get paid into different currency account(s). For{" "}
                    <Text weight={FontWeight.Bold}>Euro</Text> payments domestic
                    transaction fees will apply, for all other currencies,
                    international transaction fees will apply.
                  </Trans>
                )}
                {creditor.geo === "AU" && (
                  <Trans id="setup.bank-details.bank-options.option2-description-au">
                    Get paid into different currency account(s). For{" "}
                    <Text weight={FontWeight.Bold}>NZD</Text> payments, domestic
                    transaction fees will apply, for all other currencies,
                    international transaction fees will apply.
                  </Trans>
                )}
                {creditor.geo === "NZ" && (
                  <Trans id="setup.bank-details.bank-options.option2-description-nz">
                    Get paid into different currency account(s). For{" "}
                    <Text weight={FontWeight.Bold}>AUD</Text> payments, domestic
                    transaction fees will apply, for all other currencies,
                    international transaction fees will apply.
                  </Trans>
                )}
                {creditor.geo === "US" && (
                  <Trans id="setup.bank-details.bank-options.option2-description-us">
                    Get paid into different currency account(s). International
                    transaction fees will apply.
                  </Trans>
                )}
              </P>
            </Box>

            <Box data-testid="international-payment-bank-account-option">
              <PlainButton
                effect={HoverEffect.TextDecoration}
                textDecoration="underline"
                onClick={() => handleAddBanks()}
              >
                <Icon size="12px" name={Glyph.Add} />
                <Space layout="inline" h={0.25} />
                <Text size={3} weight={FontWeight.Bold}>
                  <Trans id="setup.bank-details.bank-options.add-bank-accounts">
                    Add bank account(s)
                  </Trans>
                </Text>
              </PlainButton>
            </Box>
          </Box>
        </XYGrid>
      </Box>
    </>
  );
};

export default InternationalPaymentsOptions;
